'use client';

import { Mastercard, Visa, Atm } from '@ahamove/icons/payment/card';
import { PaymentMethodCode, DeliveryType } from '@ahm/api-wrappers-core';
import { useLocationStore } from '@ahm/react-map/store';
import {
  Card,
  Divider,
  Radio,
  Stack,
  Title,
  rem,
  Text,
  Group,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { openContextModal } from '@mantine/modals';
import { getCookie } from 'cookies-next';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Fragment, useEffect, useMemo } from 'react';
import { ModalName } from '..';
import ButtonAddCard from '@/components/modals/payment-modal/components/button-add-card';
import PaymentSelectionItem from '@/components/modals/payment-modal/components/payment-selection-item';
import { usePaymentMethodHook } from '@/hooks';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

interface PaymentSelectionModalProps {
  onClose: () => void;
}

const PaymentSelectionModal = ({ onClose }: PaymentSelectionModalProps) => {
  const tPayment = useTranslations('payment');
  const isMobile = useMediaQuery('(max-width: 768px)');
  const {
    selectedService,
    paymentMethod: selectedPaymentMethod,
    setPaymentMethod: setSelectedPaymentMethod,
  } = useBookingStore((state) => state);
  const { validPaymentMethodList, needLinkMethod } = usePaymentMethodHook();

  const paths = useLocationStore.use.paths();

  const groupCashPM = useMemo(
    () =>
      validPaymentMethodList?.filter((pm) => {
        return (
          pm.group_type === 'cash' &&
          !(
            pm.code === PaymentMethodCode.CashByRecipient.toLowerCase() &&
            paths.length > 2
          )
        );
      }),
    [validPaymentMethodList, paths]
  );

  const groupCashLessPM = useMemo(
    () => validPaymentMethodList?.filter((pm) => pm.group_type === 'cash_less'),
    [validPaymentMethodList]
  );

  const handleSelectPaymentMethod = (value: string) => {
    const method = validPaymentMethodList?.find(
      (item) => item.method_id === value
    );

    if (method) {
      if (needLinkMethod(method)) {
        setSelectedPaymentMethod(method);
        onClose();

        openContextModal({
          modal: ModalName.LinkWallet,
          innerProps: { method },
          closeOnClickOutside: false,
          closeOnEscape: false,
          withCloseButton: false,
          centered: true,
        });
        return;
      } else {
        setSelectedPaymentMethod(method);
        onClose();
        // // detectTrackingPaymentMethod(method);
      }

      if (
        method.code === PaymentMethodCode.CashByRecipient.toLowerCase() &&
        !getCookie(ModalName.NotiCashByRecipient)
      ) {
        openContextModal({
          modal: ModalName.NotiCashByRecipient,
          innerProps: { method },
          closeOnClickOutside: false,
          closeOnEscape: false,
          withCloseButton: false,
          centered: true,
        });
        return;
      }

      setSelectedPaymentMethod(method);
      onClose();
    }
  };

  const renderPaymentMethodNotLink = () => {
    return validPaymentMethodList
      ?.filter((method) => needLinkMethod(method))
      .map((method) => (
        <Image
          src={method.icon_url}
          alt={method.code}
          key={method.method_id}
          width={24}
          height={24}
        />
      ));
  };

  useEffect(() => {
    const paymentPickedAfterEstimate = validPaymentMethodList?.find(
      (item) => item.method_id === selectedPaymentMethod?.method_id
    );
    if (
      paymentPickedAfterEstimate?.error_message_en_us ??
      paymentPickedAfterEstimate?.error_message_vi_vn
    ) {
      const balanceMethod = validPaymentMethodList?.find(
        (item) => item.code === PaymentMethodCode.Balance.toLowerCase()
      );
      setSelectedPaymentMethod(balanceMethod ?? validPaymentMethodList[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack w={'100%'} h={'100%'}>
      <Card p={0}>
        {groupCashLessPM && groupCashLessPM?.length > 0 && (
          <>
            <Radio.Group
              bg="white"
              value={selectedPaymentMethod?.method_id}
              label={
                <>
                  <Title px={isMobile ? 12 : 24} py={rem(12)} order={5}>
                    {tPayment('select_method.pay_via_cash_less')}
                  </Title>
                  <Divider />
                </>
              }
              styles={{
                label: {
                  width: '100%',
                },
              }}
            >
              {groupCashLessPM?.map((item, idx) => (
                <Fragment key={idx}>
                  <PaymentSelectionItem
                    method={item}
                    onSelect={handleSelectPaymentMethod}
                  />
                  {groupCashLessPM?.length > idx + 1 && <Divider ml={55} />}
                </Fragment>
              ))}
            </Radio.Group>
          </>
        )}
        {/* {selectedService?.delivery_type !== DeliveryType.Nationwide && (
          <ButtonAddCard />
        )} */}
      </Card>
      <Card p={0}>
        {groupCashPM && groupCashPM?.length > 0 && (
          <Radio.Group
            bg="white"
            value={selectedPaymentMethod?.method_id}
            label={
              <>
                <Title px={isMobile ? 12 : 24} py={rem(12)} order={5}>
                  {tPayment('select_method.pay_via_cash')}
                </Title>
                <Divider />
              </>
            }
            styles={{
              label: {
                width: '100%',
              },
            }}
          >
            {groupCashPM?.map((item, idx) => (
              <Fragment key={idx}>
                <PaymentSelectionItem
                  method={item}
                  onSelect={handleSelectPaymentMethod}
                />
                {groupCashPM?.length > idx + 1 && <Divider ml={55} />}
              </Fragment>
            ))}
          </Radio.Group>
        )}
      </Card>

      {selectedService?.delivery_type !== DeliveryType.Nationwide && (
        <Card pb={12} p={0}>
          <Stack px={isMobile ? 12 : 24} gap={4}>
            <Title pt={rem(12)} order={5}>
              {tPayment('select_method.link_payment_method')}
            </Title>
            <Text size="sm" c="neutral.6">
              {tPayment('select_method.link_payment_method_desc')}
            </Text>

            <Group justify="space-between">
              <Group gap={12}>
                {renderPaymentMethodNotLink()}
                <Mastercard radius={4} height={24} width={24} />
                <Atm radius={4} height={24} width={24} />
                <Visa radius={4} height={24} width={24} />
              </Group>
              <ButtonAddCard />
            </Group>
          </Stack>
        </Card>
      )}
    </Stack>
  );
};

export default PaymentSelectionModal;
